import { z } from "zod";
import { cursorQuerySchema, timestampSchema, uuidSchema } from "./api.ts";
import { anyEntityTypeSchema, csvArraySchema, queryBooleanSchema, userRefSchema } from "./common.ts";

/**
 * Notifications API — /api/v1/notifications (always scoped to the current user
 * in the current org). New notifications are also pushed over the realtime
 * stream (see realtime.ts) and, per preferences, emailed by an outbox consumer.
 */

export const NOTIFICATION_CATEGORIES = ["work", "sales", "finance", "collaboration", "workspace"] as const;
export type NotificationCategory = (typeof NOTIFICATION_CATEGORIES)[number];

export const NOTIFICATION_TYPES = [
  "task.assigned",
  "task.due_soon",
  "task.overdue",
  "task.completed",
  "followup.due",
  "followup.overdue",
  "mention",
  "comment.created",
  "file.uploaded",
  "approval.requested",
  "approval.decided",
  "lead.assigned",
  "form.submitted",
  "meeting.booked",
  "proposal.viewed",
  "proposal.accepted",
  "proposal.declined",
  "contract.signed",
  "invoice.viewed",
  "invoice.paid",
  "invoice.overdue",
  "expense.submitted",
  "expense.approved",
  "expense.rejected",
  "timesheet.submitted",
  "invitation.accepted",
  "automation.failed",
  "export.ready",
  "import.completed",
  "ai.insight",
  "security.alert",
  "system",
] as const;
export type NotificationType = (typeof NOTIFICATION_TYPES)[number];
export const notificationTypeSchema = z.enum(NOTIFICATION_TYPES);

export interface NotificationChannelPrefs {
  inApp: boolean;
  email: boolean;
}

/**
 * Catalogue used by the preferences UI and by the server for defaults.
 * `mandatory` types are always delivered in-app (the toggle is disabled).
 */
export const NOTIFICATION_TYPE_META: Record<
  NotificationType,
  { label: string; description: string; category: NotificationCategory; defaults: NotificationChannelPrefs; mandatory?: boolean }
> = {
  "task.assigned": { label: "Task assigned to me", description: "Someone assigns you a task", category: "work", defaults: { inApp: true, email: true } },
  "task.due_soon": { label: "Task due soon", description: "A task assigned to you is due within 24 hours", category: "work", defaults: { inApp: true, email: false } },
  "task.overdue": { label: "Task overdue", description: "A task assigned to you is past its due date", category: "work", defaults: { inApp: true, email: true } },
  "task.completed": { label: "Task completed", description: "A task you created or follow is completed", category: "work", defaults: { inApp: true, email: false } },
  "followup.due": { label: "Follow-up due today", description: "A follow-up assigned to you is due today", category: "sales", defaults: { inApp: true, email: false } },
  "followup.overdue": { label: "Follow-up overdue", description: "A follow-up assigned to you is past its due time", category: "sales", defaults: { inApp: true, email: true } },
  mention: { label: "Mentions", description: "Someone @mentions you", category: "collaboration", defaults: { inApp: true, email: true } },
  "comment.created": { label: "New comments", description: "New comments or client messages on records you follow", category: "collaboration", defaults: { inApp: true, email: false } },
  "file.uploaded": { label: "Client uploads", description: "A client uploads a file in the portal", category: "collaboration", defaults: { inApp: true, email: false } },
  "approval.requested": { label: "Approval requested", description: "A proof is waiting for your decision", category: "collaboration", defaults: { inApp: true, email: true } },
  "approval.decided": { label: "Approval decided", description: "A client approves a proof or asks for changes", category: "collaboration", defaults: { inApp: true, email: true } },
  "lead.assigned": { label: "Lead assigned to me", description: "A lead is assigned to you", category: "sales", defaults: { inApp: true, email: true } },
  "form.submitted": { label: "Form submissions", description: "A lead-capture form is submitted", category: "sales", defaults: { inApp: true, email: false } },
  "meeting.booked": { label: "Meeting booked", description: "Someone books time on your calendar", category: "sales", defaults: { inApp: true, email: true } },
  "proposal.viewed": { label: "Proposal viewed", description: "A client opens your proposal", category: "sales", defaults: { inApp: true, email: false } },
  "proposal.accepted": { label: "Proposal accepted", description: "A client accepts a proposal", category: "sales", defaults: { inApp: true, email: true } },
  "proposal.declined": { label: "Proposal declined", description: "A client declines a proposal", category: "sales", defaults: { inApp: true, email: true } },
  "contract.signed": { label: "Contract signed", description: "A contract is signed", category: "sales", defaults: { inApp: true, email: true } },
  "invoice.viewed": { label: "Invoice viewed", description: "A client opens an invoice", category: "finance", defaults: { inApp: true, email: false } },
  "invoice.paid": { label: "Invoice paid", description: "An invoice is paid (fully or partly)", category: "finance", defaults: { inApp: true, email: true } },
  "invoice.overdue": { label: "Invoice overdue", description: "An invoice passes its due date", category: "finance", defaults: { inApp: true, email: false } },
  "expense.submitted": { label: "Expense submitted", description: "An expense awaits your approval", category: "finance", defaults: { inApp: true, email: false } },
  "expense.approved": { label: "Expense approved", description: "Your expense was approved", category: "finance", defaults: { inApp: true, email: false } },
  "expense.rejected": { label: "Expense rejected", description: "Your expense was rejected", category: "finance", defaults: { inApp: true, email: true } },
  "timesheet.submitted": { label: "Timesheet submitted", description: "A timesheet awaits your approval", category: "work", defaults: { inApp: true, email: false } },
  "security.alert": {
    label: "Security alerts",
    description:
      "A sensitive change: the bank details on your invoices, a payment gateway, a new API key, an administrator granted, a full export, or a bulk delete",
    category: "workspace",
    defaults: { inApp: true, email: true },
    // Mandatory on purpose. The whole value of a security alert is that
    // it reaches someone who was not expecting it, and an attacker who
    // has a session can otherwise turn it off before acting.
    mandatory: true,
  },
  "invitation.accepted": { label: "Invitation accepted", description: "Someone you invited joins the workspace", category: "workspace", defaults: { inApp: true, email: false } },
  "automation.failed": { label: "Automation failed", description: "An automation you own failed to run", category: "workspace", defaults: { inApp: true, email: true } },
  "export.ready": { label: "Export ready", description: "A data export you requested is ready to download", category: "workspace", defaults: { inApp: true, email: true } },
  "import.completed": { label: "Import finished", description: "A data import you started has finished", category: "workspace", defaults: { inApp: true, email: false } },
  "ai.insight": { label: "AI insights", description: "Proactive insights and digests from Ask your business", category: "workspace", defaults: { inApp: true, email: false } },
  system: { label: "System announcements", description: "Security and account notices", category: "workspace", defaults: { inApp: true, email: true }, mandatory: true },
};

/** Notification DTO. `url` is a web path (see ENTITY_ROUTES) or null. */
export const notificationSchema = z.object({
  id: uuidSchema,
  type: notificationTypeSchema,
  title: z.string(),
  body: z.string().nullable(),
  url: z.string().nullable(),
  entityType: anyEntityTypeSchema.nullable(),
  entityId: uuidSchema.nullable(),
  actor: userRefSchema.nullable(),
  readAt: timestampSchema.nullable(),
  createdAt: timestampSchema,
});
export type NotificationDto = z.infer<typeof notificationSchema>;

/**
 * GET /api/v1/notifications?cursor&limit&unread&types → CursorPage<NotificationDto>
 * (newest first).
 */
export const listNotificationsQuerySchema = cursorQuerySchema.extend({
  unread: queryBooleanSchema.optional(),
  types: csvArraySchema(notificationTypeSchema, NOTIFICATION_TYPES.length).optional(),
});
export type ListNotificationsQuery = z.infer<typeof listNotificationsQuerySchema>;

/** GET /api/v1/notifications/unread-count → { count }. */
export const unreadCountSchema = z.object({ count: z.number().int().nonnegative() });
export type UnreadCount = z.infer<typeof unreadCountSchema>;

/**
 * POST /api/v1/notifications/:id/read, POST /api/v1/notifications/:id/unread and
 * POST /api/v1/notifications/read-all → { unreadCount } (for the badge).
 */
export const markReadResultSchema = z.object({ unreadCount: z.number().int().nonnegative() });
export type MarkReadResult = z.infer<typeof markReadResultSchema>;

/**
 * POST /api/v1/notifications/read-all — optional body. `before` limits the update
 * to notifications created at/before that instant (the newest one the user saw),
 * so a notification arriving mid-click is not silently marked read.
 */
export const readAllNotificationsInputSchema = z
  .object({ before: timestampSchema.optional() })
  .strict()
  .prefault({});

export const notificationChannelPrefsSchema = z.object({ inApp: z.boolean(), email: z.boolean() });

/** GET /api/v1/notifications/preferences → complete map (every type present). */
export const notificationPreferencesSchema = z.record(notificationTypeSchema, notificationChannelPrefsSchema);
export type NotificationPreferences = z.infer<typeof notificationPreferencesSchema>;

/**
 * PUT /api/v1/notifications/preferences → NotificationPreferences (complete map).
 * Replaces the user's preferences: types omitted revert to their defaults;
 * mandatory types keep inApp=true regardless of input.
 */
export const updateNotificationPreferencesInputSchema = z.partialRecord(notificationTypeSchema, notificationChannelPrefsSchema.strict());
export type UpdateNotificationPreferencesInput = z.infer<typeof updateNotificationPreferencesInputSchema>;

export function defaultNotificationPreferences(): NotificationPreferences {
  const out = {} as NotificationPreferences;
  for (const t of NOTIFICATION_TYPES) out[t] = { ...NOTIFICATION_TYPE_META[t].defaults };
  return out;
}

/** Pure: stored (partial) prefs → complete map with defaults + mandatory rules applied. */
export function resolveNotificationPreferences(stored: Partial<Record<string, Partial<NotificationChannelPrefs>>> | null | undefined): NotificationPreferences {
  const out = defaultNotificationPreferences();
  for (const t of NOTIFICATION_TYPES) {
    const s = stored?.[t];
    if (s) out[t] = { inApp: s.inApp ?? out[t].inApp, email: s.email ?? out[t].email };
    if (NOTIFICATION_TYPE_META[t].mandatory) out[t].inApp = true;
  }
  return out;
}
